import React, { useState, useRef } from "react";
import { error, success } from "../../../../utils/toast";
import pubsub from "sweet-pubsub";
import axios from "axios";
import { getStoredAuthToken } from "../../../../utils/authToken";
import { ImportStep, StockMode, MaterialStockRow, ValidationError } from "./types";
import { validateExcelFile, mergePrecheckErrors } from "./validation";
import { ImportStepsMaterialStock } from "./ImportStepsMaterialStock";

const showToast = (toast: any) => pubsub.emit("toast", toast);

export const ImportMaterialStockForm = () => {
    const [currentStep, setCurrentStep] = useState<ImportStep>("file");
    const [isUploading, setIsUploading] = useState(false);
    const [mode, setMode] = useState<StockMode | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [validatedRows, setValidatedRows] = useState<MaterialStockRow[]>([]);
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const [originalRowCount, setOriginalRowCount] = useState<number>(0);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const validExtensions = [".xlsx", ".xls"];
            const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf("."));

            if (!validExtensions.includes(fileExtension)) {
                error("Định dạng file không hợp lệ", "Vui lòng chọn file Excel (.xlsx hoặc .xls)");
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
                return;
            }

            setSelectedFile(file);
            // Reset validation state when new file is selected
            setValidatedRows([]);
            setValidationErrors([]);
            setOriginalRowCount(0);
        }
    };

    const runPrecheck = async (rows: any[]) => {
        const token = getStoredAuthToken();
        const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

        const items = rows.map((r: any) => ({
            row: r.rowNumber,
            material_code: r.data?.material_code,
            storage: r.data?.storage,
        }));

        const res = await axios.post(
            `${baseURL}/utils/upload-material-stocks/precheck`,
            { items },
            {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined,
                    "Content-Type": "application/json",
                },
            }
        );

        return res.data?.errors || [];
    };

    const handleNext = async () => {
        if (!selectedFile) {
            error("Vui lòng chọn file", "Bạn cần chọn một file Excel để nhập dữ liệu");
            return;
        }

        if (!mode) {
            error("Vui lòng chọn chế độ nhập", "Bạn cần chọn cách xử lý tồn kho vật tư (add hoặc set)");
            return;
        }

        if (currentStep === "file") {
            // Validate file when moving to validation step
            try {
                const { rows, errors, originalRowCount } = await validateExcelFile(selectedFile);
                
                setValidatedRows(rows);
                setValidationErrors(errors);
                setOriginalRowCount(originalRowCount || rows.length);

                // If local validation has blocking errors, stop here
                const invalidLocal = rows.filter((r) => !r.isValid).length;
                if (invalidLocal > 0 || errors.length > 0) {
                    setCurrentStep("validation");
                    return;
                }

                // Server precheck (existence + storage match)
                const precheckErrors = await runPrecheck(rows);
                const merged = mergePrecheckErrors(rows, precheckErrors);
                setValidatedRows(merged.rows);
                setValidationErrors(merged.errors);
                
                // Move to validation step to show results
                setCurrentStep("validation");
            } catch (e: any) {
                const errorMessage = e?.message || e?.toString() || "Có lỗi xảy ra khi xác thực file";
                showToast({
                    type: "danger",
                    title: "Lỗi validation file",
                    message: errorMessage,
                    duration: 5,
                });
                setSelectedFile(null);
                setValidatedRows([]);
                setValidationErrors([]);
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            }
        } else if (currentStep === "validation") {
            // Only allow moving to confirmation if there are no validation errors
            const invalidRowsCount = validatedRows.filter(r => !r.isValid).length;
            if (invalidRowsCount > 0 || validationErrors.length > 0) {
                return;
            }
            setCurrentStep("confirmation");
        }
    };

    const handleBack = () => {
        if (currentStep === "validation") {
            setCurrentStep("file");
        } else if (currentStep === "confirmation") {
            setCurrentStep("validation");
        }
    };

    const performImport = async () => {
        if (!selectedFile || !mode) return;

        setIsUploading(true);

        try {
            const formData = new FormData();
            formData.append("file", selectedFile);

            const token = getStoredAuthToken();
            const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

            const response = await axios.post(
                `${baseURL}/utils/upload-material-stocks/?mode=${mode}`,
                formData,
                {
                    headers: {
                        Authorization: token ? `Bearer ${token}` : undefined,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (response.data.status === "success") {
                const stats = response.data.stats || {};
                success(
                    "THÀNH CÔNG",
                    `Nhập tồn kho vật tư thành công. Mode: ${stats.mode || mode}, Đã cập nhật: ${stats.updated_materials || 0}, Tổng dòng: ${stats.total_rows || 0}`
                );

                // Reset form
                setSelectedFile(null);
                setMode(null);
                setValidatedRows([]);
                setValidationErrors([]);
                setOriginalRowCount(0);
                setCurrentStep("file");
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            } else {
                error("Nhập tồn kho vật tư thất bại", response.data.error || "Có lỗi xảy ra");
            }
        } catch (err: any) {
            if (err.response?.data) {
                const errorData = err.response.data;

                if (errorData.validation_errors) {
                    const errorCount = errorData.invalid_rows || errorData.validation_errors.length;
                    const errorMessages = errorData.validation_errors
                        .slice(0, 5)
                        .map((e: any) => `Dòng ${e.row}: ${e.field ? `${e.field}: ` : ""}${e.error || "Lỗi không xác định"}`)
                        .join("\n");

                    error(
                        `Có ${errorCount} dòng dữ liệu không hợp lệ`,
                        errorMessages + (errorData.validation_errors.length > 5 ? "\n..." : "")
                    );
                    setCurrentStep("validation");
                } else {
                    error(
                        "Nhập tồn kho vật tư thất bại",
                        errorData.error || errorData.message || err.message || "Có lỗi xảy ra"
                    );
                }
            } else {
                error("Nhập tồn kho vật tư thất bại", err.message || "Có lỗi xảy ra");
            }
        } finally {
            setIsUploading(false);
        }
    };

    const validRows = validatedRows.filter((r) => r.isValid);

    // Convert MaterialStockRow[] to ValidationRow[] for generic component
    const validationRows = validatedRows.map((row) => ({
        rowNumber: row.rowNumber,
        data: row.data,
        isValid: row.isValid,
        errors: row.errors,
    }));

    return (
        <div className="tw-p-6 tw-bg-white tw-rounded-lg tw-border-2 tw-border-gray-600 tw-w-full tw-box-border tw-min-w-0">
            <ImportStepsMaterialStock
                currentStep={currentStep}
                selectedFile={selectedFile}
                mode={mode}
                onModeChange={setMode}
                onFileChange={handleFileChange}
                onNext={handleNext}
                fileInputRef={fileInputRef}
                isUploading={isUploading}
                hasValidationErrors={validationErrors.length > 0}
                validatedRows={validationRows}
                onBack={handleBack}
                onImport={performImport}
                originalRowCount={originalRowCount}
            />
        </div>
    );
};


